/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { debounce } from 'lodash';
import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useGet, useMutate } from '@/net/commons';

const formSchema = z.object({
    name: z.string().pipe(z.string().min(3, { message: 'Project name must be at least 3 characters long' })),
});

type CreateProjectResponse = {
    id: number;
    name: string;
    orgId: number;
    createdAt: string;
    slug: string;
};

const CreateProject = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const form = useForm<z.infer<typeof formSchema>>({
        defaultValues: {
            name: '',
        },
        resolver: zodResolver(formSchema),
    });
    const { trigger } = useMutate<CreateProjectResponse>(`/bff/projects`, {
        revalidate: true,
        method: 'POST',
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [shouldCheckName, setShouldCheckName] = useState(false);
    const { getValues } = form;
    const { data: nameIsAvailable } = useGet<any>(
        shouldCheckName && getValues('name').length > 0
            ? `/bff/project/${encodeURIComponent(getValues('name'))}/check`
            : null,
        {
            method: 'GET',
        },
    );
    useEffect(() => {
        if (shouldCheckName && nameIsAvailable && nameIsAvailable?.available === false) {
            setErrorMessage('This name already exists.');
        } else {
            setErrorMessage('');
        }
    }, [nameIsAvailable, shouldCheckName]);

    // debounce time to not dos the server
    const handleInputChange = useCallback(
        debounce(() => {
            if (getValues('name').length > 0) {
                setShouldCheckName(true);
            }
        }, 300), // 300ms debounce time
        [],
    );
    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            setIsLoading(true);
            const res = await trigger({ name: values.name });
            navigate(`/project/${res?.slug}`);
        } catch (err) {
            setIsLoading(false);
            let errMessage = 'There was a problem with your request';
            if ('info' in (err as any) && 'code' in (err as any).info) {
                errMessage = (err as any).info.code.replaceAll('_', ' ').toLowerCase();
            }
            form.setError('name', {
                message: errMessage,
            });
        }
    };

    return (
        <div className="max-w-md w-full px-6 mx-auto my-10">
            <h1 className="text-2xl font-semibold mb-8">Create a project</h1>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-10">
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row gap-2 items-center">Project name</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="Write the name"
                                        {...field}
                                        onInput={(e) => {
                                            setErrorMessage('');
                                            setShouldCheckName(false);
                                            field.onChange(e);
                                            handleInputChange();
                                        }}
                                    />
                                </FormControl>
                                <FormMessage>{errorMessage && errorMessage}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row justify-end">
                        <Button
                            type="submit"
                            disabled={isLoading || (nameIsAvailable && nameIsAvailable?.available === false)}
                        >
                            {isLoading ? 'Creating Project...' : 'Create Project'}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
};

export default CreateProject;
